import range from 'zycommon-web/utils/range';
import { filterCombinatoricsKeybindings } from 'zycommon-web/utils/symbolic-tools/combinatorics/combinatorics-math-live';

/**
 * Build common virtual keyboard keycaps shared across symtool layouts.
 * @param {Object} options Options for building common keys.
 * @param {boolean} [options.isChallenge=false] When true, use Tab (next placeholder) instead of Check.
 * @returns {Object} The common keys.
 */
export function buildCommonKeys({ isChallenge = false } = {}) {
    const [moveLeftKeyCap, moveRightKeyCap] = [
        ['Previous', ''],
        ['Next', ' style="transform: rotate(180deg);"'],
    ].map(([direction, styling]) => ({
        class: 'action',
        command: ['performWithFeedback', `moveTo${direction}Char`],
        label: `<svg class="svg-glyph"${styling} fill="#000000" version="1.1" xmlns="https://www.w3.org/2000/svg" xmlns:xlink="https://www.w3.org/1999/xlink" viewBox="0 0 123.959 123.959" xml:space="preserve"><g id="SVGRepo_bgCarrier" stroke-width="0"></g><g id="SVGRepo_tracerCarrier" stroke-linecap="round" stroke-linejoin="round"></g><g id="SVGRepo_iconCarrier"> <g> <path d="M85.742,1.779l-56,56c-2.3,2.3-2.3,6.1,0,8.401l56,56c3.801,3.8,10.2,1.1,10.2-4.2v-112 C95.942,0.679,89.543-2.021,85.742,1.779z"></path> </g> </g></svg>`,
    }));
    const deleteKeyCap = {
        class: 'action',
        command: ['performWithFeedback', 'deleteBackward'],
        label: '<svg class="svg-glyph"><use xlink:href="#svg-delete-backward" /></svg>',
    };
    const checkKeyCap = { class: 'action check', command: ['performWithFeedback', 'commit'], label: 'Check' };
    const tabKeyCap = {
        class: 'action check',
        command: ['performWithFeedback', 'moveToNextPlaceholder'],
        label: '<svg xmlns="https://www.w3.org/2000/svg" height="24px" viewBox="0 -960 960 960" width="24px" fill="#000000"><path d="M800-240v-480h80v480h-80Zm-320 0-57-56 144-144H80v-80h487L424-664l56-56 240 240-240 240Z"/></svg>',
    };
    const nextStepKeyCap = isChallenge ? tabKeyCap : checkKeyCap;
    const separator = '[separator]';
    const fraction = '\\frac{#@}{\\placeholder}';
    const exponent = '#@^{\\placeholder}';
    const negate = { aside: 'negate', insert: '\\Negate', latex: '\\mathrm{+/-}' };

    return { moveLeftKeyCap, moveRightKeyCap, deleteKeyCap, nextStepKeyCap, separator, fraction, exponent, negate };
}

/**
 * Filter the keybindings based on the notation to limit the commands and keys available.
 * @param {Array} keybindings The current keybindings of the tool.
 * @param {string} notation The string denoting the symtool type.
 * @returns {Array}
 */
export function filterKeyBindings(keybindings, notation) {
    let newKeyBindings = keybindings;

    if (notation === 'combinatorics') {
        newKeyBindings = filterCombinatoricsKeybindings(keybindings);
    } else if (notation !== 'numeric variables' && notation !== 'symbolic equivalence') {
        const commandsToKeep = [
            'moveToPreviousChar',
            'moveToNextChar',
            'moveToMathfieldStart',
            'moveToMathfieldEnd',
            'deleteBackward',
            'deleteForward',
            'selectAll',
            'cutToClipboard',
            'copyToClipboard',
            'pasteFromClipboard',
            'undo',
            'redo',
        ];

        newKeyBindings = keybindings.filter(({ command }) => commandsToKeep.includes(command));
    }

    return newKeyBindings;
}

/**
 * Assign stable random macros to author-defined editor atomic symbols.
 * @param {Array<{key: string, latex: string}>|null|undefined} editorAtomicSymbols Editor atomic symbols to assign macros to.
 * @param {Object<string, {key: string, latex: string, macro: string}>} [latexToMacroMap={}] Map of LaTeX to macro.
 * @returns {{ symbols: Array<{key: string, latex: string, macro: string}>, latexToMacroMap: Object }}
 */
export function buildEditorAtomicSymbols(editorAtomicSymbols, latexToMacroMap = {}) {
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const longerThanLongestLatexCommandName = 50;

    const symbols =
        editorAtomicSymbols?.map(({ key, latex }) => {
            if (!(latex in latexToMacroMap)) {
                let name;
                const existingNames = Object.values(latexToMacroMap).map(({ macro }) => macro.slice(1));

                do {
                    name = range(longerThanLongestLatexCommandName)
                        .map(() => Math.floor(Math.random() * characters.length))
                        .map(index => characters.charAt(index))
                        .join('');
                } while (existingNames.includes(name));

                latexToMacroMap[latex] = { key, latex, macro: `\\${name}` };
            }

            return latexToMacroMap[latex];
        }) ?? [];

    return { symbols, latexToMacroMap };
}
